import { useDispatch } from 'react-redux';
import {
  BlockType,
  PageType,
  SectionElementType,
  VerificationFailureAction,
  VerificationType,
} from '@common/entities';
import { initializeIdIncomeSlice } from 'state/slices/idIncomeVerificationSlice';

import { getIsVerificationEnabled } from 'services';
import { Block, Field, VerificationField } from 'types/fields';
import { AnyPage } from 'types/page';
import handleError from 'utils/handleError';

import { useFetchVerificationStatus } from './useFetchVerificationStatus';
import useHandleVerificationFailedWorkFlow from './useHandleVerificationFailedWorkFlow';

export const useInitializeIDIncomeVerification = (isLeadProfile: boolean) => {
  const dispatch = useDispatch();
  const { fetchVerificationStatus } = useFetchVerificationStatus();
  const { updateStoreForFailedVerificationWorkflow } = useHandleVerificationFailedWorkFlow();

  // Type guard to check if an element is a Block
  const isBlock = (element: Block | Field): element is Block => {
    return element.elementType === SectionElementType.Block && 'customType' in element;
  };

  // Type guard for VerificationField
  const isIDVerificationBlock = (block: Block): block is VerificationField => {
    return block.customType === BlockType.IDVerification;
  };

  // Type guard for VerificationField
  const isIncomeVerificationBlock = (block: Block): block is VerificationField => {
    return block.customType === BlockType.IncomeVerification;
  };

  interface VerificationBlockLocation<T> {
    block: T;
    pageIndex: number;
  }

  interface VerificationBlocks {
    idVerification: VerificationBlockLocation<VerificationField> | null;
    incomeVerification: VerificationBlockLocation<VerificationField> | null;
  }

  const findVerificationBlocks = (pages: AnyPage[]): VerificationBlocks => {
    const result: VerificationBlocks = {
      idVerification: null,
      incomeVerification: null,
    };

    // Iterate through all pages, sections, and elements to find verification blocks
    pages.some((page, pageIndex) => {
      if (page.type !== PageType.Blank || !page.sections) return false;

      return page.sections.some((section) => {
        const elements = section.elements || [];

        return elements.some((element: Block | Field) => {
          if (!isBlock(element)) return false;

          if (isIDVerificationBlock(element) && !result.idVerification) {
            result.idVerification = {
              block: element,
              pageIndex,
            };
          } else if (isIncomeVerificationBlock(element) && !result.incomeVerification) {
            result.incomeVerification = {
              block: element,
              pageIndex,
            };
          }

          // Return true to stop iteration when both blocks are found
          return result.idVerification !== null && result.incomeVerification !== null;
        });
      });
    });

    return result;
  };

  const checkIsVerificationEnabled = async () => {
    try {
      return await getIsVerificationEnabled();
    } catch (error: any) {
      handleError('Form Viewer - ', error?.message ?? 'Error checking if verification is enabled');
      return { isIdVerificationEnabled: false, isIncomeVerificationEnabled: false };
    }
  };

  const initializeIDVerificationStore = async (pages: AnyPage[]) => {
    const returnValue = {
      isIdVerificationEnabled: false,
      isIncomeVerificationEnabled: false,
    };
    if (!pages?.length) return returnValue;

    const { idVerification, incomeVerification } = findVerificationBlocks(pages);

    // Early exit if no verification blocks found
    if (!idVerification && !incomeVerification) return returnValue;

    const { isIdVerificationEnabled, isIncomeVerificationEnabled } =
      await checkIsVerificationEnabled();

    const payload: any = {
      isIdVerificationEnabled,
      isIncomeVerificationEnabled,
    };

    if (idVerification) {
      const { block, pageIndex } = idVerification;
      const idConfig = block.configuration;
      payload.idVerification = {
        isRequired: idConfig.required,
        fieldPageIndex: pageIndex,
        status: null,
        metadata: null,
        verificationType: VerificationType.IdentityVerification,
        verificationFailureAction:
          idConfig?.verificationFailureAction || VerificationFailureAction.DoNothing,
      };
    }

    if (incomeVerification) {
      const { block, pageIndex } = incomeVerification;
      const incomeConfig = block.configuration;
      payload.incomeVerification = {
        isRequired: incomeConfig.required,
        fieldPageIndex: pageIndex,
        verificationFailureAction:
          incomeConfig?.verificationFailureAction || VerificationFailureAction.DoNothing,
        status: null,
        metadata: null,
        verificationType: VerificationType.IncomeVerification,
      };
    }

    const { idVerification: idVerificationStatus, incomeVerification: incomeVerificationStatus } =
      await fetchVerificationStatus();

    // Only set status if the verification block exists
    if (payload.idVerification) {
      payload.idVerification.status = idVerificationStatus.status;
      payload.idVerification.verificationType = idVerificationStatus.verificationType;
      if (!isLeadProfile) {
        updateStoreForFailedVerificationWorkflow(
          idVerificationStatus.status,
          payload.idVerification?.verificationFailureAction,
        );
      }
    }

    if (payload.incomeVerification) {
      payload.incomeVerification.status = incomeVerificationStatus.status;
      payload.incomeVerification.verificationType =
        incomeVerificationStatus.verificationType || VerificationType.IncomeVerification;

      if (!isLeadProfile) {
        updateStoreForFailedVerificationWorkflow(
          incomeVerificationStatus.status,
          payload.incomeVerification?.verificationFailureAction,
        );
      }
    }

    dispatch(initializeIdIncomeSlice(payload));
    return { isIdVerificationEnabled, isIncomeVerificationEnabled };
  };

  return initializeIDVerificationStore;
};
